import { ImageFromServer } from '@/components/images/ImageFromServer';
import { ExerciseDTO, MuscleIntensity } from '@/generated';
import { useBodyZoneListQuery, useMuscleTagsQuery } from '@/hooks/query-hooks';
import { Intensity } from '@/models/intensity';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { findRelatedExercises } from '../exercise-utils';

type CopyMusclesModalProps = {
  show: boolean;
  progressionExerciseIds: string[];
  regressionExerciseIds: string[];
  exercises: ExerciseDTO[];
  onHide: () => void;
  onCopy: (values: { mainBodyZoneId: string; muscleIntensities: MuscleIntensity[] }) => void;
};

export const CopyMusclesModal = (props: CopyMusclesModalProps) => {
  const [sourceExerciseId, setSourceExerciseId] = useState<string>();
  const { bodyZoneList } = useBodyZoneListQuery();
  const { muscleTagMap } = useMuscleTagsQuery();
  const sourceExercises = useMemo(
    () => findRelatedExercises(props.progressionExerciseIds, props.regressionExerciseIds, props.exercises),
    [props.progressionExerciseIds, props.regressionExerciseIds, props.exercises],
  );
  const sourceExercise = sourceExercises.find((exercise) => exercise.id === sourceExerciseId);
  const bodyZone = bodyZoneList?.find((candidate) => candidate.id === sourceExercise?.mainBodyZoneId);

  useEffect(() => {
    if (props.show) setSourceExerciseId(undefined);
  }, [props.show]);

  return (
    <Dialog open={props.show} onClose={props.onHide} fullWidth maxWidth="md">
      <DialogTitle>Copy muscles and body zone</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(220px, 1fr) minmax(280px, 1.4fr)' }, gap: 3, minHeight: 300 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Progressions and regressions
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 1 }}>
              {sourceExercises.map((exercise) => (
                <Paper
                  key={exercise.id}
                  component="button"
                  type="button"
                  aria-pressed={sourceExerciseId === exercise.id}
                  onClick={() => setSourceExerciseId(exercise.id)}
                  variant="outlined"
                  sx={{
                    borderColor: sourceExerciseId === exercise.id ? 'common.white' : 'divider',
                    bgcolor: sourceExerciseId === exercise.id ? 'action.selected' : 'background.paper',
                    color: 'text.primary',
                    cursor: 'pointer',
                    p: 1,
                    minWidth: 0,
                  }}
                >
                  <ImageFromServer id={exercise.imageId} thumbnail alt="" sx={{ width: 72, height: 72, mx: 'auto', borderRadius: 1 }} />
                  <Typography variant="caption" component="div" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                    {exercise.name}
                  </Typography>
                </Paper>
              ))}
            </Box>
            {!sourceExercises.length && <Typography color="text.secondary">No progressions or regressions found.</Typography>}
          </Box>

          <Box sx={{ borderLeft: { sm: 1 }, borderColor: { sm: 'divider' }, pl: { sm: 3 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {sourceExercise ? `Muscles of ${sourceExercise.name}` : 'Muscles and body zone'}
            </Typography>
            {!sourceExercise && <Typography color="text.secondary">Select an exercise on the left.</Typography>}
            {sourceExercise && (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Default body zone
                  </Typography>
                  <Typography>{bodyZone?.name ?? 'Not selected'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    Muscle intensities
                  </Typography>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {sourceExercise.muscleIntensities.map((muscleIntensity) => (
                      <Chip
                        key={muscleIntensity.muscleId}
                        label={`${muscleTagMap?.get(muscleIntensity.muscleId)?.name ?? muscleIntensity.muscleId} · ${Intensity[muscleIntensity.intensity]}`}
                      />
                    ))}
                    {!sourceExercise.muscleIntensities.length && <Typography color="text.secondary">No muscles configured.</Typography>}
                  </Stack>
                </Box>
              </Stack>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onHide}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!sourceExercise}
          onClick={() => {
            if (!sourceExercise) return;
            props.onCopy({
              mainBodyZoneId: sourceExercise.mainBodyZoneId,
              muscleIntensities: sourceExercise.muscleIntensities.map((muscleIntensity) => ({ ...muscleIntensity })),
            });
          }}
        >
          Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
};
